import * as React from 'react';
import {
  FC,
  forwardRef,
  PropsWithChildren,
  useEffect,
  useState,
} from 'react';
import classNames from 'classnames';
import { Fade } from 'react-bootstrap';
import { Variant } from '@collaborator/npm_modules/dist/css/variant';
import { Else, If, Then } from 'react-if';

type AlertProps = {
  variant?: Variant;
  additionalClassName?: string;
  dismissible?: boolean;
  isOpen?: boolean;
  onClose?: () => void | Promise<void>;
  // eslint-disable-next-line react/no-unused-prop-types
  ref?: React.ForwardedRef<HTMLDivElement>,
};

const Alert: FC<PropsWithChildren<AlertProps>> = forwardRef<HTMLDivElement, AlertProps>(({
  children,
  variant = Variant.success,
  additionalClassName,
  dismissible,
  isOpen: isOpenFromProps = true,
  onClose,
}, forwardedRef) => {
  const [isOpen, setIsOpen] = useState<boolean>(isOpenFromProps);
  const [isCloseInProcess, setIsCloseInProcess] = useState<boolean>(false);
  const [isDisplayNone, setIsDisplayNone] = useState<boolean>(!isOpenFromProps);

  useEffect(() => {
    setIsOpen(isOpenFromProps);
  }, [isOpenFromProps]);

  const handlerClose = () => {
    if (!onClose) {
      setIsOpen(false);
      return;
    }

    const res = onClose();
    if (!res) {
      setIsOpen(false);
      return;
    }

    setIsCloseInProcess(true);
    res.then(() => {
      setIsOpen(false);
    }).catch(() => {
      setIsCloseInProcess(false);
    });
  };

  const renderCloseButton = () => {
    if (!dismissible) {
      return null;
    }

    return (
      <button type="button" className="close" data-dismiss="alert" aria-label="Close" onClick={handlerClose}>
        <If condition={isCloseInProcess}>
          <Then>
            <i className="fas fa-spin fa-spinner fs-16" />
          </Then>
          <Else>
            <i className="fal fa-times fs-16" />
          </Else>
        </If>
      </button>
    );
  };

  const handlerFadeOnEnter = () => {
    setIsDisplayNone(false);
  };

  const handlerFadeOnExited = () => {
    setIsDisplayNone(true);
  };

  return (
    <Fade in={isOpen} onEnter={handlerFadeOnEnter} onExited={handlerFadeOnExited}>
      <div
        ref={forwardedRef}
        className={classNames(
          `alert alert-${variant}`,
          { [additionalClassName]: additionalClassName },
          { 'alert-dismissible': dismissible },
          { 'd-none': isDisplayNone },
        )}
      >
        {children}
        {renderCloseButton()}
      </div>
    </Fade>
  );
});

export { Alert };
